import React, { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Home, Package, Receipt, Calculator, Menu, Lock } from 'lucide-react';
import axios from 'axios';
import { useShippingCount } from '../ShippingCountContext';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:3001') + '/api';
const TIER_RANK = { free: 0, starter: 1, dealer: 2, pro: 3, business: 4 };

// 5-item bottom tab bar, visible on phones only via CSS (.mobile-bottom-nav).
// Triggers the existing sidebar drawer for "More" so we don't duplicate nav.
function fireOpenMenu() {
  window.dispatchEvent(new CustomEvent('scn-open-mobile-menu'));
}

const ITEMS = [
  { to: '/',           label: 'Home',       Icon: Home,       exact: true, tourTab: 'home' },
  { to: '/inventory',  label: 'Inventory',  Icon: Package, tourTab: 'inventory' },
  { to: '/orders',     label: 'Orders',     Icon: Receipt, tourTab: 'orders' },
  { to: '/accounting', label: 'Money',      Icon: Calculator, tourTab: 'accounting' },
];

// Watches body[data-tour-tab] so the MobileTour can highlight which bottom-
// nav tab the current step is talking about. Without this, page transitions
// feel invisible, the user doesn't realize the tour just navigated for them.
function useTourTab() {
  const [tourTab, setTourTab] = useState(() =>
    typeof document !== 'undefined' ? document.body.getAttribute('data-tour-tab') : null,
  );
  useEffect(() => {
    const obs = new MutationObserver(() => {
      setTourTab(document.body.getAttribute('data-tour-tab'));
    });
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-tour-tab'] });
    return () => obs.disconnect();
  }, []);
  return tourTab;
}

export default function MobileBottomNav() {
  const location = useLocation();
  const tourTab = useTourTab();
  const { needsShipping, pendingOffers } = useShippingCount();
  const ordersCount = needsShipping + pendingOffers; // to ship + pending eBay offers
  // "Money" (Accounting) is Dealer+; badge it locked for lower tiers so a tap
  // isn't a surprise paywall. Same rank logic the sidebar uses.
  const [moneyLocked, setMoneyLocked] = useState(false);
  useEffect(() => {
    axios.get(`${API}/stripe/subscription`).then(r => {
      const d = r.data || {};
      const rank = d.trialActive ? 99 : (d.paymentFailing ? 0 : (TIER_RANK[d.tier] ?? 0));
      setMoneyLocked(rank < TIER_RANK.dealer);
    }).catch(() => {});
  }, []);

  return (
    <nav className="mobile-bottom-nav" aria-label="Primary navigation">
      {ITEMS.map(({ to, label, Icon, exact, tourTab: itemTab }) => {
        const active = exact ? location.pathname === to : location.pathname.startsWith(to);
        const isTourActive = tourTab === itemTab;
        const shipBadge = to === '/orders' && ordersCount > 0;
        return (
          <NavLink
            key={to}
            to={to}
            end={exact}
            className={`mbn-item ${active ? 'mbn-active' : ''} ${isTourActive ? 'mbn-tour-active' : ''}`}
          >
            <span className="mbn-icon-wrap">
              <Icon size={22} strokeWidth={active ? 2.4 : 1.85} />
              {shipBadge && <span className="mbn-ship-badge">{ordersCount > 99 ? '99+' : ordersCount}</span>}
              {to === '/accounting' && moneyLocked && <span className="mbn-lock-badge" title="Dealer feature"><Lock size={9} strokeWidth={2.75} /></span>}
            </span>
            <span className="mbn-label">{label}</span>
          </NavLink>
        );
      })}
      <button
        type="button"
        className={`mbn-item ${tourTab === 'more' ? 'mbn-tour-active' : ''}`}
        onClick={fireOpenMenu}
        aria-label="More menu"
      >
        <Menu size={22} strokeWidth={1.85} />
        <span className="mbn-label">More</span>
      </button>
    </nav>
  );
}
